import React, { useMemo, useRef } from 'react';
import styles from './MultiFileUpload.module.scss';
import Text from '../../../Text';
import useTranslation from '../../../../../../Hooks/useTranslation';

type Props = {
	value?: File[] | null;
	onChange?: (files: File[]) => void;
	name?: string;
	accept?: string;
	disabled?: boolean;
};

const MultiFileUpload: React.FC<Props> = ({
	value = null,
	onChange,
	name,
	accept,
	disabled,
}) => {
	const { t } = useTranslation();
	const hiddenFileInput = useRef<HTMLInputElement | null>(null);
	const filesCount = value?.length ?? 0;
	const label = useMemo(() => {
		switch (filesCount) {
			case 0:
				return null;
			case 1:
				return value?.[0]?.name ?? null;
			case 2:
			case 3:
			case 4:
				return t('base.file.two', { filesCount: filesCount.toString() });
			default:
				return t('base.file.many', { filesCount: filesCount.toString() });
		}
	}, [filesCount, value]);

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.currentTarget.files ?? []);
		onChange?.(files);

		e.currentTarget.value = '';
	};

	const handleFilePick = () => {
		if (!disabled) hiddenFileInput.current?.click();
	};

	return (
		<div className={styles.wrapper}>
			<div
				className={styles.uploadButton}
				onClick={handleFilePick}
				aria-disabled={disabled}
			>
				{label ? (
					<div className={styles.fileName}>
						{label.length > 35 ? label.substring(0, 35).concat('...') : label}
					</div>
				) : (
					<div className={styles.placeholder}>
						<Text tx='base.shared.pickFile' />
						<i className='fa-solid fa-angle-right' aria-hidden='true'></i>
					</div>
				)}
			</div>

			<input
				ref={hiddenFileInput}
				name={name}
				type='file'
				multiple
				accept={accept}
				disabled={disabled}
				onChange={handleOnChange}
				className={styles.input}
			/>
		</div>
	);
};

export default MultiFileUpload;
